"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AIChatbot } from "./AIChatbot";
import { JobCriteria } from "./JobCriteria";
import { RoleJDTable } from "./RoleJDTable";
import { FinalizeJD } from "./FinalizeJD";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
interface ProjectRequirements {
  directors_count?: number;
  managers_count?: number;
  team_members_count?: number;
  organizational_structure?: string;
  project_id?: string;
}
export const DefineRoles = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId');
  const [projectName, setProjectName] = useState<string>("");
  const [projectRequirements, setProjectRequirements] = useState<ProjectRequirements>({});
  const [selectedCriteria, setSelectedCriteria] = useState({
    country: '',
    experience: '',
    site: '',
    profession: '',
    discipline: '',
    role_type: '',
    employment_type: ''
  });
  const [currentJobDescription, setCurrentJobDescription] = useState("");
  const [showFinalizeJD, setShowFinalizeJD] = useState(false);
  const [refreshRoleJDTable, setRefreshRoleJDTable] = useState(0);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState<number>(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<'table' | 'criteria' | 'ai'>('table');
  const [hasRolesWithJD, setHasRolesWithJD] = useState(false);
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!urlProjectId) {
        toast.error("Please select a project first");
        return;
      }
      try {
        const {
          data: projectData,
          error: projectError
        } = await supabase.from('project_requirements').select('name, directors_count, managers_count, team_members_count, organizational_structure, project_id').eq('id', urlProjectId).maybeSingle();
        if (projectError) throw projectError;
        if (!projectData) {
          toast.error("Project not found");
          return;
        }
        setProjectName(projectData.name);
        setProjectRequirements(projectData);
        if (projectData.project_id) {
          console.log('Fetching roles for project:', projectData.project_id);
          const {
            data: rolesData,
            error: rolesError
          } = await supabase.from('role_jd').select('has_jd').eq('project_id', projectData.project_id);
          if (rolesError) {
            console.error('Error fetching roles:', rolesError);
            throw rolesError;
          }
          console.log('Roles data:', rolesData);
          const hasJD = rolesData?.some(role => role.has_jd === true) || false;
          console.log('Has JD:', hasJD);
          setHasRolesWithJD(hasJD);
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
        toast.error("Failed to fetch project details");
      }
    };
    fetchProjectDetails();
  }, [urlProjectId, refreshRoleJDTable]);
  const handleCriteriaChange = (values: typeof selectedCriteria) => {
    setSelectedCriteria(values);
  };
  const handleJobDescriptionChange = (description: string) => {
    setCurrentJobDescription(description);
  };
  const handleAddToRole = () => {
    if (!currentJobDescription) {
      toast.error("Please generate a job description first");
      return;
    }
    setShowFinalizeJD(true);
  };
  const handleFinalizeJD = (editedDescription: string) => {
    setRefreshRoleJDTable(prev => prev + 1);
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTo({
          left: 970,
          behavior: 'smooth'
        });
        setActiveSection('ai');
      }
    }
  };
  const handleBack = async () => {
    if (!urlProjectId) {
      toast.error("Project ID is missing");
      return;
    }
    try {
      const {
        data: projectData,
        error: projectError
      } = await supabase.from('project_requirements').select('id').eq('project_id', urlProjectId).maybeSingle();
      if (projectError) {
        console.error('Error fetching project data:', projectError);
        toast.error("Failed to fetch project data");
        return;
      }
      if (!projectData?.id) {
        toast.error("No project ID found");
        return;
      }
      navigate(`/dashboard?step=2&projectId=${projectData.id}`);
      toast.success("Returning to Organization Structure Editor");
    } catch (error) {
      console.error('Error in handleBack:', error);
      toast.error("Failed to navigate back");
    }
  };
  const handleNext = async () => {
    if (!urlProjectId) return;
    try {
      console.log('Checking roles before navigation');
      const {
        data: roles,
        error: rolesError
      } = await supabase.from('role_jd').select('id, has_jd').eq('project_id', urlProjectId).eq('has_jd', true);
      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        toast.error("Failed to process roles");
        return;
      }
      console.log('Roles with JD:', roles);
      if (!roles || roles.length === 0) {
        toast.error("No roles with job descriptions found. Please finalize at least one job description.");
        return;
      }
      navigate(`/dashboard?step=4&projectId=${urlProjectId}`);
    } catch (error) {
      console.error('Error in handleNext:', error);
      toast.error("Failed to navigate to next step");
    }
  };
  const handleRoleSelect = (roleId: string, index: number) => {
    setSelectedRoleId(roleId);
    setSelectedRoleIndex(index);
  };
  const scrollToCriteria = () => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTo({
          left: 560,
          behavior: 'smooth'
        });
        setActiveSection('criteria');
      }
    }
  };
  const scrollToAIGenerator = () => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTo({
          left: 1180,
          behavior: 'smooth'
        });
        setActiveSection('ai');
      }
    }
  };
  const scrollToRoleJDTable = () => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      const tableSection = viewport?.querySelector('[data-role-jd-table-section]');
      if (viewport && tableSection) {
        const viewportWidth = viewport.clientWidth;
        const sectionWidth = (tableSection as HTMLElement).offsetWidth;
        const sectionLeft = (tableSection as HTMLElement).offsetLeft;
        const scrollPosition = sectionLeft - viewportWidth / 2 + sectionWidth / 2;
        viewport.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
        setActiveSection('table');
      }
    }
  };
  useEffect(() => {
    const centerTableSection = () => {
      if (!scrollAreaRef.current) return;
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (!viewport) return;
      viewport.scrollLeft = 40;
      setActiveSection('table');
    };
    centerTableSection();
    const timers = [setTimeout(centerTableSection, 0), setTimeout(centerTableSection, 50), setTimeout(centerTableSection, 100), setTimeout(centerTableSection, 200), setTimeout(centerTableSection, 500), setTimeout(centerTableSection, 1000)];
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);
  return <div className="space-y-2 mt-10 max-w-[1400px] mx-auto">
      <ScrollArea className="w-full whitespace-nowrap rounded-md" ref={scrollAreaRef}>
        <div className="flex items-stretch gap-0 p-0 py-0 px-0 h-[470px]">
          <div className="w-[400px] min-w-[400px]" />
          <RoleJDTable key={refreshRoleJDTable} onRoleSelect={handleRoleSelect} className={cn("w-[44.5%] min-w-[500px] mb-8 pb-[10px]", activeSection === 'table' ? "border-2 border-primary" : "border border-primary/20")} data-role-jd-table-section />
          <div className="flex flex-col items-center justify-center px-8">
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button className="w-0 h-0 
                      border-t-[25px] border-t-transparent 
                      border-l-[50px] border-l-white/70 
                      border-b-[25px] border-b-transparent
                      hover:border-l-white
                      transition-all duration-200
                      transform hover:scale-105
                      active:scale-95
                      focus:outline-none focus:ring-2 focus:ring-white/50" onClick={scrollToCriteria}>
                    <span className="sr-only">Scroll to criteria section</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-black/70 text-white border-none">
                  Click to center the Job Criteria section
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <JobCriteria onCriteriaChange={handleCriteriaChange} className={cn("w-[7%] min-w-[250px] mb-8 pb-[10px]", activeSection === 'criteria' ? "border-2 border-primary" : "border border-primary/20")} data-criteria-section />
          <div className="flex flex-col items-center justify-center px-8">
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button className="w-0 h-0 
                      border-t-[25px] border-t-transparent 
                      border-l-[50px] border-l-white/70
                      border-b-[25px] border-b-transparent
                      hover:border-l-white
                      transition-all duration-200
                      transform hover:scale-105
                      active:scale-95
                      focus:outline-none focus:ring-2 focus:ring-white/50" onClick={scrollToAIGenerator}>
                    <span className="sr-only">Scroll to AI Generator section</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-black/70 text-white border-none">
                  Click to center the AI JD Generator section
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <AIChatbot onJobDescriptionChange={handleJobDescriptionChange} selectedCriteria={selectedCriteria} className={cn("w-[44.5%] min-w-[400px] mb-8 pb-[10px]", activeSection === 'ai' ? "border-2 border-primary" : "border border-primary/20")} data-ai-generator-section />
          <div className="flex flex-col items-center justify-center px-8">
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3">
                    <button className="w-0 h-0 
                        border-t-[25px] border-t-transparent 
                        border-l-[50px] border-l-primary
                        border-b-[25px] border-b-transparent
                        hover:border-l-primary/80
                        transition-all duration-200
                        transform hover:scale-105
                        active:scale-95
                        focus:outline-none focus:ring-2 focus:ring-primary/50" onClick={handleAddToRole}>
                      <span className="sr-only">Add to role</span>
                    </button>
                    <span className="text-white whitespace-nowrap">Click to add JD to role</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-black/70 text-white border-none">
                  Click to add the job description to the Role JD Table
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="w-[400px] min-w-[400px]" />
        </div>
        <ScrollBar orientation="horizontal" className="bg-primary/10 h-3" />
      </ScrollArea>

      <Separator className="my-2 bg-primary/20" />
      
       
      <div className="flex justify-center items-center gap-4 my-0 py-[8px]">
        <Button onClick={handleBack} className="w-32 bg-white text-secondary hover:bg-white/90">
          Back
        </Button>
        <Button onClick={handleNext} className="w-32">
          Next
        </Button>
      </div>

      <FinalizeJD open={showFinalizeJD} onOpenChange={setShowFinalizeJD} selectedValues={selectedCriteria} jobDescription={currentJobDescription} onConfirm={handleFinalizeJD} onSuccess={() => setRefreshRoleJDTable(prev => prev + 1)} selectedRoleId={selectedRoleId} selectedRoleIndex={selectedRoleIndex} />
    </div>;
};
